import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Edit, Mail, Send, Eye, Clock, ToggleLeft, ToggleRight,
  BarChart3, ListTodo, Users, Banknote, CalendarClock, X, Check, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_ICONS = {
  financials_company: BarChart3,
  financials_per_store: BarChart3,
  tasks_overview: ListTodo,
  tasks_per_store: ListTodo,
  tasks_personal: ListTodo,
  open_positions: Users,
  upcoming_payments: Banknote,
};

const SCHEDULE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly (1st)" },
];

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const emptyDigest = {
  name: "", description: "", schedule: "weekly", day_of_week: "monday",
  enabled: true, recipients: [], content_blocks: [], store_scope: "all",
  specific_store_id: null,
};

export default function EmailDigests() {
  const [digests, setDigests] = useState([]);
  const [blocks, setBlocks] = useState({});
  const [stores, setStores] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDigest);
  const [newEmail, setNewEmail] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [sending, setSending] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dRes, bRes, sRes, tRes] = await Promise.all([
        axios.get(`${API}/digests`),
        axios.get(`${API}/digests/content-blocks`),
        axios.get(`${API}/stores`),
        axios.get(`${API}/team`),
      ]);
      setDigests(dRes.data || []);
      setBlocks(bRes.data || {});
      setStores(sRes.data || []);
      setTeamMembers(tRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyDigest });
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name, description: d.description || "", schedule: d.schedule,
      day_of_week: d.day_of_week || "monday", enabled: d.enabled,
      recipients: [...(d.recipients || [])],
      content_blocks: [...(d.content_blocks || [])],
      store_scope: d.store_scope || "all",
      specific_store_id: d.specific_store_id || null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.content_blocks.length === 0) { toast.error("Select at least one content block"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/digests/${editing.id}`, form);
        toast.success("Digest updated");
      } else {
        await axios.post(`${API}/digests`, form);
        toast.success("Digest created");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) { toast.error("Failed to save digest"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this email digest?")) return;
    try {
      await axios.delete(`${API}/digests/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const toggleEnabled = async (d) => {
    try {
      await axios.put(`${API}/digests/${d.id}`, { ...d, enabled: !d.enabled });
      fetchAll();
    } catch (e) { toast.error("Failed to toggle"); }
  };

  const handlePreview = async (id) => {
    try {
      const res = await axios.post(`${API}/digests/${id}/preview`);
      setPreviewText(res.data.preview);
      setPreviewSubject(res.data.subject);
      setPreviewOpen(true);
    } catch (e) { toast.error("Failed to generate preview"); }
  };

  const handleSend = async (id) => {
    setSending(id);
    try {
      const res = await axios.post(`${API}/digests/${id}/send`);
      if (res.data.sent > 0) {
        toast.success(`Sent to ${res.data.sent} of ${res.data.total} recipients`);
      } else {
        toast.error("Failed to send to any recipients");
      }
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(e => toast.error(e));
      }
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setSending(null); }
  };

  const toggleBlock = (key) => {
    setForm(prev => ({
      ...prev,
      content_blocks: prev.content_blocks.includes(key)
        ? prev.content_blocks.filter(k => k !== key)
        : [...prev.content_blocks, key],
    }));
  };

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (form.recipients.includes(email)) { toast.error("Already added"); return; }
    setForm(prev => ({ ...prev, recipients: [...prev.recipients, email] }));
    setNewEmail("");
  };

  const removeRecipient = (email) => {
    setForm(prev => ({ ...prev, recipients: prev.recipients.filter(e => e !== email) }));
  };

  const addTeamMember = (email) => {
    if (form.recipients.includes(email)) return;
    setForm(prev => ({ ...prev, recipients: [...prev.recipients, email] }));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="email-digests-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Automated Emails</span>
          </div>
          <h1 data-testid="digests-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>Email Digests</h1>
          <p className="text-base text-stone-500 mt-2">Create automated email reports with customizable content for different audiences.</p>
        </div>
        <Button data-testid="create-digest-btn" onClick={openCreate} className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all font-medium px-5">
          <Plus className="w-4 h-4 mr-1.5" /> New Digest
        </Button>
      </div>

      {/* Digests List */}
      {digests.length === 0 ? (
        <div className="text-center py-20">
          <Mail className="w-14 h-14 text-stone-200 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-700 mb-2">No email digests yet</h3>
          <p className="text-sm text-stone-500 mb-6 max-w-md mx-auto">Create automated email reports to keep your team informed. Mix and match content blocks like financials, tasks, and open positions.</p>
          <Button onClick={openCreate} className="bg-stone-900 text-stone-50 hover:bg-stone-800"><Plus className="w-4 h-4 mr-1.5" /> Create First Digest</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {digests.map(d => (
            <Card key={d.id} data-testid={`digest-${d.id}`} className={`bg-white border rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all hover:shadow-md ${d.enabled ? "border-stone-200/60" : "border-stone-200/40 opacity-70"}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-stone-900 truncate">{d.name}</h3>
                    {d.description && <p className="text-xs text-stone-400 mt-0.5 truncate">{d.description}</p>}
                  </div>
                  <button data-testid={`toggle-digest-${d.id}`} onClick={() => toggleEnabled(d)} className="flex-shrink-0 ml-2">
                    {d.enabled ? <ToggleRight className="w-7 h-7 text-emerald-500" /> : <ToggleLeft className="w-7 h-7 text-stone-300" />}
                  </button>
                </div>

                {/* Schedule */}
                <div className="flex items-center gap-2 text-xs text-stone-500 mb-3">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="capitalize">{d.schedule}{d.schedule === "weekly" && d.day_of_week ? ` (${d.day_of_week})` : ""}</span>
                  <span className="text-stone-300">|</span>
                  <span>{d.recipients?.length || 0} recipients</span>
                </div>

                {/* Content blocks */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {(d.content_blocks || []).map(key => {
                    const Icon = BLOCK_ICONS[key] || Mail;
                    const info = blocks[key];
                    return (
                      <span key={key} className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-md text-xs text-stone-600">
                        <Icon className="w-3 h-3" /> {info?.label || key}
                      </span>
                    );
                  })}
                </div>

                {/* Last sent */}
                {d.last_sent && (
                  <p className="text-xs text-stone-400 mb-3">Last sent: {new Date(d.last_sent).toLocaleDateString()}{d.send_count > 0 && ` (${d.send_count}x)`}</p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-stone-100">
                  <Button data-testid={`send-digest-${d.id}`} variant="outline" size="sm" onClick={() => handleSend(d.id)} disabled={sending === d.id || !d.recipients?.length} className="text-xs">
                    {sending === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send Now
                  </Button>
                  <Button data-testid={`preview-digest-${d.id}`} variant="ghost" size="sm" onClick={() => handlePreview(d.id)} className="text-xs text-stone-500">
                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                  </Button>
                  <div className="ml-auto flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(d)} className="text-stone-400 hover:text-stone-700 px-2"><Edit className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(d.id)} className="text-stone-400 hover:text-red-600 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
              {editing ? "Edit Email Digest" : "Create Email Digest"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Name & Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600">Name *</Label>
                <Input data-testid="digest-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Directors Weekly Report" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Description</Label>
                <Input data-testid="digest-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." className="mt-1" />
              </div>
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600">Frequency</Label>
                <Select value={form.schedule} onValueChange={v => setForm({ ...form, schedule: v })}>
                  <SelectTrigger data-testid="digest-schedule" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{SCHEDULE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(form.schedule === "weekly" || form.schedule === "biweekly") && (
                <div>
                  <Label className="text-xs text-stone-600">Day of Week</Label>
                  <Select value={form.day_of_week} onValueChange={v => setForm({ ...form, day_of_week: v })}>
                    <SelectTrigger data-testid="digest-day" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d} className="capitalize">{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Content Blocks */}
            <div>
              <Label className="text-xs text-stone-600 mb-2 block">Content Blocks *</Label>
              <p className="text-xs text-stone-400 mb-3">Select which sections to include in this digest.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(blocks).map(([key, info]) => {
                  const Icon = BLOCK_ICONS[key] || Mail;
                  const selected = form.content_blocks.includes(key);
                  return (
                    <button
                      key={key}
                      data-testid={`block-${key}`}
                      type="button"
                      onClick={() => toggleBlock(key)}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${selected ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? "bg-stone-900" : "bg-stone-100"}`}>
                        <Icon className={`w-4 h-4 ${selected ? "text-stone-50" : "text-stone-500"}`} />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${selected ? "text-stone-900" : "text-stone-600"}`}>{info.label}</p>
                        <p className="text-xs text-stone-400">{info.description}</p>
                      </div>
                      {selected && <Check className="w-4 h-4 text-stone-900 flex-shrink-0 mt-1" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recipients */}
            <div>
              <Label className="text-xs text-stone-600 mb-2 block">Recipients</Label>
              
              {/* Quick add from team */}
              {teamMembers.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-stone-400 mb-2">Quick add from team:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {teamMembers.filter(m => m.email && !form.recipients.includes(m.email.toLowerCase())).slice(0, 8).map(m => (
                      <button key={m.id || m.email} type="button" onClick={() => addTeamMember(m.email.toLowerCase())}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-stone-100 hover:bg-stone-200 rounded-full text-xs text-stone-600 transition-colors">
                        <Plus className="w-3 h-3" /> {m.name || m.email}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual add */}
              <div className="flex gap-2">
                <Input
                  data-testid="digest-email-input"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                  placeholder="Enter email address..."
                  className="flex-1"
                />
                <Button type="button" onClick={addRecipient} variant="outline" size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Recipient list */}
              {form.recipients.length > 0 && (
                <div className="mt-2 space-y-1">
                  {form.recipients.map(email => (
                    <div key={email} className="flex items-center justify-between px-3 py-1.5 bg-stone-50 rounded-lg">
                      <span className="text-sm text-stone-700">{email}</span>
                      <button type="button" onClick={() => removeRecipient(email)} className="text-stone-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save */}
            <Button data-testid="digest-save-btn" onClick={handleSave} className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium">
              {editing ? "Update Digest" : "Create Digest"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <p className="text-xs text-stone-500 mb-1">Subject:</p>
            <p className="text-sm font-medium text-stone-800 mb-4 pb-3 border-b border-stone-200">{previewSubject}</p>
            <pre data-testid="digest-preview-text" className="text-sm text-stone-700 whitespace-pre-wrap font-mono bg-stone-50 p-4 rounded-lg border border-stone-200 leading-relaxed">{previewText}</pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
